/**
 * Сетка вкладки: чистые функции раскладки.
 *
 * Элемент раскладки — `{ id, x, y, w, h }` в единицах сетки: столбец и строка левого верхнего
 * угла, ширина в столбцах и высота в строках. Сетка ведёт себя как в Grafana: элементы всплывают
 * вверх, пустых строк нет. Поэтому одна и та же функция уплотнения работает и в просмотре, и в
 * редакторе — две реализации разошлись бы, и админ сохранял бы не то, что потом видит клиент.
 *
 * Модулем, а не внутри компонента, по правилу проекта: компоненты юнит-тестами не покрываются,
 * а здесь вся логика, которую стоит проверять.
 */

export const DEFAULT_COLUMNS = 12;
export const COLUMN_CHOICES = [12, 24];
export const MAX_COLUMNS = 24;

/** Шаг сетки по вертикали: строка 16 px и зазор 24 px — элемент высотой h занимает 40h − 24 px. */
export const GRID_ROW_PX = 16;
export const GRID_GAP_X_PX = 16;
export const GRID_GAP_Y_PX = 24;

/** Сервер не примет больше; лишнее отбрасывается с конца — первыми уходят сироты. */
export const MAX_LAYOUT_ITEMS = 200;

/**
 * Элементы, которых нет в сохранённой раскладке (новый график пакета), встают под всеми
 * остальными в порядке умолчания: смещение заведомо ниже любой сохранённой строки.
 */
const BELOW_SAVED_OFFSET = 100000;

const overlapsColumns = (a, b) => a.x < b.x + b.w && b.x < a.x + a.w;

/** Ширина в пределах `[minW, cols]`, элемент целиком внутри сетки. */
export const clampItem = (item, cols, minW = 1) => {
  const w = Math.max(Math.min(minW, cols), Math.min(Math.round(item.w) || 1, cols));
  const x = Math.min(Math.max(0, Math.round(item.x) || 0), cols - w);
  return {
    ...item,
    x,
    w,
    y: Math.max(0, Math.round(item.y) || 0),
    h: Math.max(1, Math.round(item.h) || 1)
  };
};

/** Порядок чтения: сверху вниз, в строке слева направо. Он же порядок в DOM. */
export const readingOrder = (items) =>
  [...items].sort((a, b) => a.y - b.y || a.x - b.x);

/**
 * Вертикальное уплотнение. Элементы обходятся в порядке чтения, каждый ложится сразу под самым
 * нижним из уже уложенных элементов, с которыми делит столбцы. Пустых строк после этого нет,
 * а относительный порядок сохраняется.
 *
 * `priorityId` выигрывает ничью по строке: переносимый элемент встаёт на целевое место, а
 * стоявший там уходит ниже. Без этого перенос на строку соседа ничего бы не менял.
 */
export const compact = (items, { priorityId } = {}) => {
  const ordered = items
    .map((item, index) => ({ item, index }))
    .sort((a, b) =>
      a.item.y - b.item.y
      || (a.item.id === priorityId ? -1 : 0) - (b.item.id === priorityId ? -1 : 0)
      || a.item.x - b.item.x
      || a.index - b.index);

  const placed = [];
  for (const { item } of ordered) {
    let y = 0;
    for (const other of placed) {
      if (overlapsColumns(item, other)) {
        y = Math.max(y, other.y + other.h);
      }
    }
    placed.push({ ...item, y });
  }

  return placed;
};

/** Перенос элемента в клетку `(x, y)`; соседи уплотняются вокруг него. */
export const moveItem = (items, id, { x, y }, cols) =>
  compact(
    items.map((item) => (item.id === id ? clampItem({ ...item, x, y }, cols, item.w) : item)),
    { priorityId: id }
  );

/**
 * Новый размер элемента. Расширение у правого края сдвигает элемент влево, а не упирается:
 * иначе широкий элемент в последнем столбце нельзя было бы растянуть вовсе.
 */
export const resizeItem = (items, id, { w, h }, cols, minW = 1) =>
  compact(
    items.map((item) => {
      if (item.id !== id) {
        return item;
      }

      const width = Math.max(Math.min(minW, cols), Math.min(w, cols));
      return clampItem({ ...item, w: width, h, x: Math.min(item.x, cols - width) }, cols, minW);
    }),
    { priorityId: id }
  );

/**
 * Шаг с клавиатуры. Влево и вправо — на столбец. Вверх и вниз — через ближайшего соседа в тех
 * же столбцах: шаг на строку при уплотнении ничего бы не менял, элемент всплывал бы обратно.
 */
export const stepItem = (items, id, direction, cols) => {
  const target = items.find((item) => item.id === id);
  if (!target) {
    return items;
  }

  if (direction === 'left' || direction === 'right') {
    const x = target.x + (direction === 'left' ? -1 : 1);
    return moveItem(items, id, { x, y: target.y }, cols);
  }

  const neighbours = items.filter((item) => item.id !== id && overlapsColumns(item, target));
  if (direction === 'up') {
    const above = neighbours
      .filter((item) => item.y < target.y)
      .sort((a, b) => b.y - a.y || b.x - a.x)[0];
    return above ? moveItem(items, id, { x: target.x, y: above.y }, cols) : items;
  }

  const below = neighbours
    .filter((item) => item.y > target.y)
    .sort((a, b) => a.y - b.y || a.x - b.x)[0];
  // Строка ниже соседа: при уплотнении он поднимется на место элемента, элемент ляжет под него.
  return below ? moveItem(items, id, { x: target.x, y: below.y + 1 }, cols) : items;
};

/**
 * Позиции и ширины в другом числе столбцов, без уплотнения: так масштабируются элементы,
 * которых сейчас нет на экране, — их позиции переносятся в запись как есть.
 */
export const scaleItems = (items, fromCols, toCols) => {
  if (fromCols === toCols) {
    return items;
  }

  const factor = toCols / fromCols;
  return items.map((item) => clampItem({
    ...item,
    x: Math.round(item.x * factor),
    w: Math.max(1, Math.round(item.w * factor))
  }, toCols));
};

/** Смена числа столбцов: позиции и ширины масштабируются, затем уплотняются. */
export const scaleColumns = (items, fromCols, toCols) =>
  fromCols === toCols ? items : compact(scaleItems(items, fromCols, toCols));

/** Ширина умолчания задаётся в 12 столбцах; для другой сетки — пропорционально. */
export const scaleWidth = (width, cols) => Math.max(1, Math.round((width * cols) / DEFAULT_COLUMNS));

const scaleDefaults = (defaults, cols) =>
  cols === DEFAULT_COLUMNS
    ? defaults
    : defaults.map((item) => ({ ...item, x: scaleWidth(item.x, cols), w: scaleWidth(item.w, cols) }));

/**
 * Все известные вкладке элементы с позициями: сохранённое накладывается на умолчание, элемент
 * без сохранённой позиции встаёт под всеми в порядке умолчания. Сироты — сохранённые
 * элементы, которых вкладка не знает (карточка, которую убрал новый пакет), — отдаются
 * отдельно: на экране их нет, но из записи они не вычищаются, как и переименования.
 */
export const mergeWithDefaults = (defaults, saved) => {
  const columns = saved?.columns ?? DEFAULT_COLUMNS;
  const savedById = new Map((saved?.items ?? []).map((item) => [item.id, item]));
  const knownIds = new Set(defaults.map((item) => item.id));

  const items = scaleDefaults(defaults, columns).map((item) => {
    const stored = savedById.get(item.id);
    if (stored) {
      return { id: item.id, x: stored.x, y: stored.y, w: stored.w, h: stored.h };
    }

    return saved
      ? { id: item.id, x: item.x, y: BELOW_SAVED_OFFSET + item.y, w: item.w, h: item.h }
      : { id: item.id, x: item.x, y: item.y, w: item.w, h: item.h };
  });

  const orphans = (saved?.items ?? []).filter((item) => !knownIds.has(item.id));
  return { columns, items, orphans };
};

/**
 * Что видно на вкладке: из известных элементов остаются нарисованные, каждый ширины не меньше
 * своего минимума, и всё уплотняется — скрытая панель дыры не оставляет.
 */
export const visibleLayout = (items, visibleIds, cols, minWidthOf = () => 1) =>
  compact(items
    .filter((item) => visibleIds.has(item.id))
    .map((item) => clampItem(item, cols, minWidthOf(item.id))));

/**
 * Снимок для записи: видимые элементы в уплотнённых позициях, остальные известные и сироты —
 * как были. Сохраняется всё, иначе элемент, которого не было на экране в момент сохранения,
 * потом уехал бы в конец вкладки.
 */
export const layoutSnapshot = ({ visible, rest, orphans }) =>
  [...visible, ...rest, ...orphans]
    .slice(0, MAX_LAYOUT_ITEMS)
    .map(({ id, x, y, w, h }) => ({ id, x, y, w, h }));

/**
 * Элементы, которых нет даже в умолчании (раздел нарисовал то, что не описал), встают под всеми
 * во всю ширину. Это страховка: без неё такой элемент молча пропал бы с вкладки.
 */
export const ensureItems = (items, ids, cols) => {
  const known = new Set(items.map((item) => item.id));
  const missing = [...ids]
    .filter((id) => !known.has(id))
    .map((id, index) => ({ id, x: 0, y: BELOW_SAVED_OFFSET * 2 + index, w: cols, h: 1 }));
  return missing.length > 0 ? [...items, ...missing] : items;
};

/**
 * Черновик редактора — `{ columns, items, orphans, baseRevision }`: `items` — все известные
 * вкладке элементы, видимые и нет. Операция применяется к видимым в уплотнённых позициях,
 * остальные переносятся как были.
 */
export const updateVisible = (draft, visibleIds, minWidthOf, operation) => {
  const visible = visibleLayout(draft.items, visibleIds, draft.columns, minWidthOf);
  const rest = draft.items.filter((item) => !visibleIds.has(item.id));
  return { ...draft, items: [...operation(visible, draft.columns), ...rest] };
};

/** Смена числа столбцов черновика: видимые масштабируются с уплотнением, остальные — как есть. */
export const changeDraftColumns = (draft, visibleIds, minWidthOf, toCols) => {
  const visible = visibleLayout(draft.items, visibleIds, draft.columns, minWidthOf);
  const rest = draft.items.filter((item) => !visibleIds.has(item.id));
  return {
    ...draft,
    columns: toCols,
    items: [...scaleColumns(visible, draft.columns, toCols), ...scaleItems(rest, draft.columns, toCols)],
    orphans: scaleItems(draft.orphans, draft.columns, toCols)
  };
};

/** Снимок черновика для записи на сервер. */
export const draftSnapshot = (draft, visibleIds, minWidthOf) =>
  layoutSnapshot({
    visible: visibleLayout(draft.items, visibleIds, draft.columns, minWidthOf),
    rest: draft.items.filter((item) => !visibleIds.has(item.id)),
    orphans: draft.orphans
  });

/** Совпадают ли две раскладки по месту и размеру каждого элемента. */
export const sameLayout = (a, b) => {
  if (a.length !== b.length) {
    return false;
  }

  const byId = new Map(b.map((item) => [item.id, item]));
  return a.every((item) => {
    const other = byId.get(item.id);
    return other && other.x === item.x && other.y === item.y && other.w === item.w && other.h === item.h;
  });
};

/**
 * Раскладка умолчания для потока карточек: слева направо с переносом, как раскладывает их
 * CSS Grid без явных позиций. Нечётная половинная карточка перед полноширинной оставляет
 * пустое место — так же, как сегодняшняя сетка секции графиков.
 */
export const flowPlace = (entries, { startY = 0, cols = DEFAULT_COLUMNS } = {}) => {
  const placed = [];
  let x = 0;
  let y = startY;
  let rowHeight = 0;

  for (const entry of entries) {
    const w = Math.min(entry.w, cols);
    if (x + w > cols) {
      y += rowHeight;
      x = 0;
      rowHeight = 0;
    }

    placed.push({ id: entry.id, x, y, w, h: entry.h });
    x += w;
    rowHeight = Math.max(rowHeight, entry.h);
  }

  return placed;
};

/** Нижняя граница раскладки — первая свободная строка под всеми элементами. */
export const layoutBottom = (items) =>
  items.reduce((bottom, item) => Math.max(bottom, item.y + item.h), 0);

/** `"16px 40px 16px"` → `[16, 40, 16]`. Так `getComputedStyle` отдаёт дорожки сетки. */
export const parseTrackSizes = (value) =>
  String(value ?? '')
    .split(/\s+/)
    .map((token) => Number.parseFloat(token))
    .filter((size) => Number.isFinite(size) && size >= 0);

/**
 * Клетка сетки под указателем. Геометрия снимается один раз в начале переноса: дальше раскладка
 * меняется, строки растут и сжимаются, и пересчёт по живой сетке перекидывал бы элемент
 * туда-обратно между двумя клетками.
 */
export const pointerToCell = ({ clientX, clientY }, geometry) => {
  const { left, top, width, cols, rowSizes, gapX = GRID_GAP_X_PX, gapY = GRID_GAP_Y_PX } = geometry;
  const columnWidth = (width - gapX * (cols - 1)) / cols;
  const column = Math.floor((clientX - left + gapX / 2) / (columnWidth + gapX));

  let row = 0;
  let edge = top;
  const offset = clientY;
  for (;;) {
    const size = rowSizes[row] ?? GRID_ROW_PX;
    if (offset < edge + size + gapY / 2 || row > 100000) {
      break;
    }

    edge += size + gapY;
    row += 1;
  }

  return {
    column: Math.min(Math.max(column, 0), cols - 1),
    row: Math.max(row, 0)
  };
};
